import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { workingHoursEndpoints } from '../../services/api/endpoints/workingHours.endpoint';
import { WorkingHour } from '../../types/workingHours';
import { logger } from '../../utils/logger';

export const useWorkingHoursActions = () => {
  const [workingHours, setWorkingHours] = useState<Partial<WorkingHour>[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedOrganizationId = useSelector(
    (state: RootState) => state.organizationContext.selectedOrganizationId
  );

  const createWorkingHours = async (newWorkingHours: Partial<WorkingHour>[]) => {
    if (!selectedOrganizationId || !newWorkingHours.length) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    setError(null);

    try {
      const formattedWorkingHours = newWorkingHours.map(wh => ({
        orgId: selectedOrganizationId,
        ...wh,
      }));

      const results = await workingHoursEndpoints.createMultipleWorkingHours(
        selectedOrganizationId,
        formattedWorkingHours
      );

      setWorkingHours(results);
      logger.info('Working hours created successfully');
      return results;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create working hours';
      setError(message);
      logger.error('Error creating working hours:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateWorkingHours = async (updatedWorkingHours: WorkingHour[]) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization or resource selected');
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create new working hours
      const formattedWorkingHours = updatedWorkingHours.map(wh => ({
        ...wh,
        orgId: selectedOrganizationId,
      }));

      const results = await workingHoursEndpoints.updateWorkingHours(
        selectedOrganizationId,
        formattedWorkingHours
      );

      setWorkingHours(results);
      logger.info('Working hours updated successfully');
      return results;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update working hours';
      setError(message);
      logger.error('Error updating working hours:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // array of working hour ids
  const deleteWorkingHour = async (workingHourId: number[]) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization or resource selected');
    }

    setIsLoading(true);
    setError(null);

    try {
      const promises = workingHourId.map(whId =>
        workingHoursEndpoints.deleteWorkingHour(selectedOrganizationId, whId)
      );
      await Promise.all(promises);
      workingHourId.forEach(whId => {
        setWorkingHours(prev => prev.filter(wh => wh.id !== whId));
      });
      logger.info('Working hour deleted successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete working hour';
      setError(message);
      logger.error('Error deleting working hour:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    workingHours,
    isLoading,
    error,
    selectedOrganizationId,
    createWorkingHours,
    updateWorkingHours,
    deleteWorkingHour,
  };
};
